/*
 * Tracks the saved-to-account project that the editor is currently working on.
 *
 * The editor starts out "unsaved": the work in the workspace does not exist in
 * the user's account yet. The first successful save links the workspace to a
 * cloud project (id + name), and every later save updates that same project
 * instead of creating a new one. Opening a project from the account links the
 * workspace to it as well.
 *
 * Only the link lives here; the project bytes always come from the VM, so this
 * state can never drift out of sync with what the user actually sees.
 */
const SAVE_START = 'openblock-gui/cloudProject/SAVE_START';
const SAVE_SUCCESS = 'openblock-gui/cloudProject/SAVE_SUCCESS';
const SAVE_FAILURE = 'openblock-gui/cloudProject/SAVE_FAILURE';
const LINK = 'openblock-gui/cloudProject/LINK';
const UNLINK = 'openblock-gui/cloudProject/UNLINK';
const RENAME = 'openblock-gui/cloudProject/RENAME';

const initialState = {
    // The account-side project this workspace is linked to, or null when the
    // current work has never been saved.
    projectId: null,
    name: null,
    isSaving: false,
    // Timestamp of the last successful save, used to show "已保存" feedback.
    savedAt: null,
    error: null
};

const reducer = function (state = initialState, action) {
    switch (action.type) {
    case SAVE_START:
        return Object.assign({}, state, {
            isSaving: true,
            error: null
        });
    case SAVE_SUCCESS:
        return Object.assign({}, state, {
            projectId: action.projectId,
            name: action.name,
            isSaving: false,
            savedAt: action.savedAt,
            error: null
        });
    case SAVE_FAILURE:
        return Object.assign({}, state, {
            isSaving: false,
            error: action.error
        });
    case LINK:
        // Opening a saved project makes it the save target straight away.
        return Object.assign({}, state, {
            projectId: action.projectId,
            name: action.name,
            isSaving: false,
            savedAt: null,
            error: null
        });
    case UNLINK:
        // Signing out must not leave one user's project id attached to the
        // next user's session.
        return initialState;
    case RENAME:
        // Renaming the project the editor is linked to must not detach it: the
        // save dialog pre-fills from this name, so it has to follow along.
        // Renaming any other project leaves this link untouched.
        if (String(state.projectId) !== String(action.projectId)) return state;
        return Object.assign({}, state, {name: action.name});
    default:
        return state;
    }
};

const saveStart = function () {
    return {type: SAVE_START};
};

const saveSuccess = function (projectId, name) {
    return {type: SAVE_SUCCESS, projectId: projectId, name: name, savedAt: Date.now()};
};

const saveFailure = function (error) {
    return {type: SAVE_FAILURE, error: error};
};

const linkCloudProject = function (projectId, name) {
    return {type: LINK, projectId: projectId, name: name};
};

const unlinkCloudProject = function () {
    return {type: UNLINK};
};

const renameCloudProject = function (projectId, name) {
    return {type: RENAME, projectId: projectId, name: name};
};

const getCloudProjectId = function (cloudProject) {
    return cloudProject.projectId;
};

const getCloudProjectName = function (cloudProject) {
    return cloudProject.name;
};

const getIsSavingProject = function (cloudProject) {
    return cloudProject.isSaving;
};

export {
    reducer as default,
    initialState as cloudProjectInitialState,
    saveStart,
    saveSuccess,
    saveFailure,
    linkCloudProject,
    unlinkCloudProject,
    renameCloudProject,
    getCloudProjectId,
    getCloudProjectName,
    getIsSavingProject
};
